/**
 * The four marks this surface draws, as inline SVG and CSS.
 *
 * ── SVG, NOT CANVAS ──────────────────────────────────────────────────────
 * These pages get screenshotted into board decks and exported to PDF. SVG
 * survives a screenshot and a print at any zoom; canvas does not. The panel
 * this replaces drew on a canvas inside a `useEffect(..., [])`, so every chart
 * rendered once and then lied whenever a filter changed. Nothing here holds
 * state: the mark is a pure function of its props, so a filter change is a
 * re-render and cannot desynchronise.
 *
 * ── FOUR MARKS, AND WHY NOT MORE ─────────────────────────────────────────
 * Ranked bars, a filled trend, paired rates and a segmented bar. That is the
 * whole vocabulary, because it is the whole vocabulary the data supports. No
 * donuts of invented outcome rates, no funnel whose bars are drawn at lengths
 * that disagree with their own labels, no dual axis.
 *
 * ── A LENGTH IS A CLAIM ──────────────────────────────────────────────────
 * A suppressed row is never drawn at a plausible width, never hatched and
 * never ghosted. It gets a short neutral floor rule that is visibly not a bar,
 * and its label stays, because hiding the row would hide that the category
 * exists, which is a worse distortion than hiding the number.
 *
 * ── EVERY MARK CARRIES ITS DATA IN TEXT ──────────────────────────────────
 * An `aria-label` naming the measure and the period, plus a visually hidden
 * table of exactly the rows the chart draws. WCAG 2.1 AA is a procurement gate
 * with a date on it, and a chart with no readable data is the single most
 * common finding in a higher-education accessibility audit.
 */

import type { ReactNode } from "react";

import {
  Card,
  CellValue,
  LeadFigure,
  SUPPRESSED_MARKER,
  VisuallyHiddenTable,
  type Provenance,
  type Tone,
} from "~/app/components/primitives";
import { cn } from "~/app/lib/cn";
import { formatCount, formatPercent } from "~/app/lib/format";
import type { Distribution, DistRow } from "~/app/lib/demo-data";
import type { Cell, Rate } from "~/lib/suppression";

/** The categorical ramp, by token. Hue never encodes valence here: a category
 *  colour and a semantic colour never appear in the same mark. */
export const SERIES = [
  "var(--cat-1)",
  "var(--cat-2)",
  "var(--cat-3)",
  "var(--cat-4)",
  "var(--cat-5)",
  "var(--cat-6)",
] as const;

function cellText(cell: Cell<number>): string {
  if (cell.kind === "value") return formatCount(cell.value);
  if (cell.kind === "suppressed") return SUPPRESSED_MARKER;
  return "Not available";
}

/* ==========================================================================
   Ranked bars

   The workhorse of a small-data dashboard, because it degrades gracefully: at
   two rows it is still a chart, where a donut at two slices is a joke.
   ========================================================================== */

export function RankedList({
  distribution,
  label,
  color = SERIES[0],
}: {
  distribution: Distribution;
  /** Names the measure and the period for assistive technology. */
  label: string;
  color?: string;
}) {
  const rows: DistRow[] = [...distribution.rows];
  if (distribution.remainder) rows.push(distribution.remainder);

  return (
    <div>
      <ul className="e-ranked" aria-hidden="true">
        {rows.map((row) => (
          <li key={row.label}>
            <div className="e-ranked-head">
              <span className="e-ranked-label">{row.label}</span>
              <span className="e-ranked-value">
                <CellValue
                  cell={row.cell}
                  format={formatCount}
                  label={row.label}
                />
              </span>
            </div>
            {row.length === null ? (
              <div className="e-ranked-floor" />
            ) : (
              <div className="e-ranked-track">
                <div
                  className="e-ranked-fill"
                  style={{
                    width: `${Math.max(row.length * 100, 1.5).toFixed(1)}%`,
                    background: row.isRemainder ? "var(--neutral-fill)" : color,
                  }}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="e-a11y-clip">
        <VisuallyHiddenTable
          caption={label}
          columns={["Group", distribution.unit === "mentors" ? "Mentors" : "Students"]}
          rows={rows.map((row) => [row.label, cellText(row.cell)])}
        />
      </div>
    </div>
  );
}

/* ==========================================================================
   Trend

   Weeks end on Sunday and the current partial week is not drawn. That is not a
   preference: a part-week renders as a cliff, every reader reads a cliff as a
   collapse, and it is enforced in the data layer rather than in a runbook.
   ========================================================================== */

const VIEW_W = 320;
const VIEW_H = 132;
const PAD_L = 6;
const PAD_B = 16;
/* Every stroke is non-scaling, because the SVG stretches to its slot. Markers
   are zero-length round-capped strokes rather than circles, which the stretch
   would squash into ovals. */
const NON_SCALING = { vectorEffect: "non-scaling-stroke" } as const;

/** A round axis maximum, with even steps so the middle gridline is a whole
 *  number: these are counts of people and "7.5 students" is not a reading. */
function niceMax(raw: number): number {
  if (raw <= 4) return 4;
  if (raw <= 6) return 6;
  if (raw <= 8) return 8;
  if (raw <= 10) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  for (const step of [1, 2, 3, 4, 5, 6, 8, 10]) {
    if (raw <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

export interface TrendPoint {
  readonly label: string;
  readonly value: number;
}

export function TrendArea({
  points,
  label,
  color = SERIES[0],
  annotations = [],
  unit = "students",
}: {
  points: readonly TrendPoint[];
  label: string;
  color?: string;
  /** Partner-written markers. They render as vertical rules on every time
   *  series, which is how a partner can ask whether their own intervention
   *  changed anything without P3 asserting that it did. */
  annotations?: readonly { readonly label: string; readonly at: number }[];
  unit?: string;
}) {
  const max = niceMax(Math.max(...points.map((p) => p.value), 1));
  const gradientId = `e-trend-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;

  const x = (i: number) =>
    PAD_L + (i / Math.max(points.length - 1, 1)) * (VIEW_W - PAD_L - 6);
  const y = (v: number) => VIEW_H - PAD_B - (v / max) * (VIEW_H - PAD_B - 14);

  const line = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`)
    .join("");
  // The fill closes straight down under the first and last points. It closed
  // at VIEW_W until Sep 11 2026, six units right of the last point, and the
  // stretched plot drew that as a slanted right edge (Thomas's screenshot).
  const baseline = (VIEW_H - PAD_B).toFixed(1);
  const area = `${line}L${x(points.length - 1).toFixed(1)},${baseline}L${x(0).toFixed(1)},${baseline}Z`;

  const tickIndexes =
    points.length <= 5
      ? points.map((_, i) => i)
      : [0, 1, 2, 3, 4].map((k) => Math.round((k * (points.length - 1)) / 4));

  return (
    <div className="e-trend">
      <div className="e-trend-y" aria-hidden="true">
        {[max, max / 2].map((v) => (
          <span key={v} style={{ top: `${((y(v) / VIEW_H) * 100).toFixed(2)}%` }}>
            {formatCount(v)}
          </span>
        ))}
      </div>

      <div className="e-trend-plot">
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={label}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.24" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>

          {[max, max / 2].map((v) => (
            <line
              key={v}
              x1={PAD_L}
              y1={y(v)}
              x2={VIEW_W}
              y2={y(v)}
              stroke="var(--chart-grid)"
              {...NON_SCALING}
            />
          ))}
          <line
            x1={PAD_L}
            y1={VIEW_H - PAD_B}
            x2={VIEW_W}
            y2={VIEW_H - PAD_B}
            stroke="var(--chart-grid)"
            {...NON_SCALING}
          />

          {annotations.map((a) => (
            <line
              key={a.label}
              x1={PAD_L + a.at * (VIEW_W - PAD_L - 6)}
              y1={6}
              x2={PAD_L + a.at * (VIEW_W - PAD_L - 6)}
              y2={VIEW_H - PAD_B}
              stroke="var(--accent)"
              strokeDasharray="3 3"
              strokeOpacity="0.55"
              {...NON_SCALING}
            />
          ))}

          <path d={area} fill={`url(#${gradientId})`} />
          <path
            d={line}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            {...NON_SCALING}
          />
          {points.map((p, i) => (
            <path
              key={p.label}
              d={`M${x(i).toFixed(1)},${y(p.value).toFixed(1)}h0.01`}
              stroke={color}
              strokeWidth="4.4"
              strokeLinecap="round"
              {...NON_SCALING}
            />
          ))}
        </svg>
      </div>

      {annotations.length > 0 ? (
        <div className="e-anno-labels" aria-hidden="true">
          {annotations.map((a) => (
            <span
              key={a.label}
              /* Clamped away from the edges: a label centred on the first or
                 last week is half outside the plot, and a marker that reads as
                 clipped reads as broken. */
              style={{
                left: `${Math.min(Math.max(a.at * 100, 6), 94).toFixed(2)}%`,
              }}
            >
              {a.label}
            </span>
          ))}
        </div>
      ) : null}

      <div className="e-trend-x" aria-hidden="true">
        {tickIndexes.map((i) => {
          const point = points[i];
          if (!point) return null;
          return (
            <span key={point.label} style={{ left: `${((x(i) / VIEW_W) * 100).toFixed(2)}%` }}>
              {point.label}
            </span>
          );
        })}
      </div>

      <div className="e-a11y-clip">
        <VisuallyHiddenTable
          caption={label}
          columns={["Week ending", `Cumulative ${unit}`]}
          rows={points.map((p) => [p.label, formatCount(p.value)])}
        />
      </div>
    </div>
  );
}

/* ==========================================================================
   Paired rates

   Two populations, one 0 to 100 scale, one row each. This is the honest
   replacement for a shelf of uncited benchmark pills: P3 computes both sides
   of every row drawn here, and the caption says it is a comparison between
   groups rather than a measure of what a partnership caused.
   ========================================================================== */

export interface PairedRow {
  readonly name: string;
  readonly sub: string;
  readonly cell: Cell<Rate>;
  readonly color: string;
}

export function PairedRates({
  rows,
  label,
}: {
  rows: readonly PairedRow[];
  label: string;
}) {
  return (
    <div>
      <ul className="e-paired" aria-hidden="true">
        {rows.map((row) => {
          const fraction = row.cell.kind === "value" ? row.cell.value.rate : null;
          return (
            <li key={row.name}>
              <div className="e-paired-head">
                <div>
                  <p className="e-paired-name">{row.name}</p>
                  <p className="e-paired-sub">{row.sub}</p>
                </div>
                <span className="e-figure">
                  <CellValue
                    cell={row.cell}
                    format={(r) => formatPercent(r.rate)}
                    label={row.name}
                  />
                </span>
              </div>
              {fraction === null ? (
                <div className="e-ranked-floor" />
              ) : (
                <div className="e-ranked-track">
                  <div
                    className="e-ranked-fill"
                    style={{
                      width: `${Math.max(fraction * 100, 1.5).toFixed(1)}%`,
                      background: row.color,
                    }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="e-a11y-clip">
        <VisuallyHiddenTable
          caption={label}
          columns={["Group", "Share", "Counted from"]}
          rows={rows.map((row) => [
            row.name,
            row.cell.kind === "value"
              ? formatPercent(row.cell.value.rate)
              : row.cell.kind === "suppressed"
                ? SUPPRESSED_MARKER
                : "Not available",
            row.sub,
          ])}
        />
      </div>
    </div>
  );
}

/* ==========================================================================
   Segmented bar

   One row, parts of one whole, for a status split. Only used where the parts
   genuinely sum to the whole, which is why it is not used for the support
   areas: a student can pick more than one, so those rows sum to selections and
   a stacked bar would assert a share of a population that does not exist.
   ========================================================================== */

export interface Segment {
  readonly label: string;
  readonly cell: Cell<number>;
  readonly color: string;
}

export function SegmentedBar({
  segments,
  label,
}: {
  segments: readonly Segment[];
  label: string;
}) {
  const drawable = segments.flatMap((s) =>
    s.cell.kind === "value" && s.cell.value > 0
      ? [{ label: s.label, value: s.cell.value, color: s.color }]
      : [],
  );
  const total = drawable.reduce((sum, s) => sum + s.value, 0);

  return (
    <div>
      <div className="e-segbar" aria-hidden="true">
        {total > 0
          ? drawable.map((s) => (
              <i
                key={s.label}
                style={{
                  width: `${((s.value / total) * 100).toFixed(2)}%`,
                  background: s.color,
                }}
              />
            ))
          : null}
      </div>
      <ul className="e-segkey" aria-hidden="true">
        {segments.map((s) => (
          <li key={s.label}>
            <b style={{ background: s.color }} />
            {s.label}
            <span>
              <CellValue cell={s.cell} format={formatCount} label={s.label} />
            </span>
          </li>
        ))}
      </ul>
      <div className="e-a11y-clip">
        <VisuallyHiddenTable
          caption={label}
          columns={["Group", "Students"]}
          rows={segments.map((s) => [s.label, cellText(s.cell)])}
        />
      </div>
    </div>
  );
}

/* ==========================================================================
   Small shared furniture
   ========================================================================== */

/**
 * A KPI tile. One lead figure, one note pinned to the bottom so a row of four
 * shares a baseline whether or not each carries a delta.
 *
 * The accent tile's colour comes from a movement, so a red dot never appears
 * without a falling number behind it. With no comparison available, every tile
 * is neutral, which is the honest state for a partner in their first term.
 */
export function KpiTile<T>({
  label,
  cell,
  format,
  provenance,
  note,
  tone = "neutral",
  hue,
  icon,
  delta,
}: {
  label: string;
  cell: Cell<T>;
  format: (value: T) => ReactNode;
  provenance: Provenance;
  note: ReactNode;
  tone?: Tone;
  /** A categorical hue for the tile, the member app's four (crimson, plum,
   *  green, amber). It marks the card, never a status: with no comparison
   *  period there is no direction to encode, and a grey dot on every tile
   *  read as a row of disabled controls (Thomas, Sep 9 2026). */
  hue?: 1 | 2 | 3 | 4;
  /** A 24-box glyph for the tile. Falls back to the dot. */
  icon?: ReactNode;
  delta?: ReactNode;
}) {
  return (
    <Card variant="kpi">
      <div className="e-kpi-top">
        <p className="e-label e-kpi-label">{label}</p>
        <span
          className={cn("e-kpi-tile", hue ? `hue-${hue}` : `tone-${tone}`)}
          aria-hidden="true"
        >
          {icon ?? <i />}
        </span>
      </div>
      <LeadFigure cell={cell} format={format} label={label} provenance={provenance} />
      {delta ? <div className="e-kpi-delta">{delta}</div> : null}
      <p className="e-kpi-note">{note}</p>
    </Card>
  );
}

/** A label and a value on one line, rules between. Used where a card holds
 *  two or three facts that are not a chart. */
export function DefinitionRows({
  rows,
}: {
  rows: readonly { label: string; value: ReactNode }[];
}) {
  return (
    <dl className="e-defs">
      {rows.map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
